/**
 * Admin Login Page
 * Authentication page for admin users
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { adminAuthService } from '@/services/adminAuthService';
import { adminHomePath } from '@/utils/adminHome';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

// Validation schema
const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'El email es requerido')
    .email('Email inválido'),
  password: z
    .string()
    .min(6, 'La contraseña debe tener al menos 6 caracteres'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const { trackAdminAuth } = useAnalytics();

  // Get the location user was trying to access before redirect
  // Si venía de una página concreta, volver a ella; si no, a su página de inicio según el rol
  const from = (location.state as { from?: Location })?.from?.pathname;

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormValues) => {
    try {
      setIsLoading(true);

      const response = await adminAuthService.login(data.email, data.password);

      trackAdminAuth.loginSuccess(response.admin.selected_restaurant_id);
      toast.success('Sesión iniciada correctamente');

      // Redirect to the page user was trying to access, or dashboard
      navigate(from || adminHomePath(response.admin.role), { replace: true });
    } catch (error) {
      console.error('Login error:', error);

      // Handle different error types
      let errorMessage = 'Error al iniciar sesión. Inténtalo de nuevo.';
      const apiError = error as { response?: { status?: number } };
      if (apiError.response?.status === 401) {
        errorMessage = 'Credenciales incorrectas';
        toast.error(errorMessage);
      } else if (apiError.response?.status === 404) {
        errorMessage = 'Servidor no disponible';
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage);
      }
      trackAdminAuth.loginFailure(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#eeeeee] px-4">
      <div className="w-full max-w-md bg-white rounded-sm shadow-sm border border-gray-200">
        {/* Header */}
        <div className="px-8 pt-12 pb-8">
          <div className="text-center mb-8">
            <h1 className="text-[24px] font-normal tracking-[0.5px] text-[#1e1e1e] uppercase mb-1">
              KOMBU
            </h1>
            <div className="h-[1px] bg-[#1e1e1e] mx-auto mt-4 mb-6"></div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-[20px] font-light text-[#1e1e1e] mb-2">
              Panel de Administración
            </h2>
            <p className="text-[14px] font-light text-[#9d9c9c]">
              Ingresa tus credenciales para acceder
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="px-8 pb-12">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[12px] font-medium text-[#1e1e1e] uppercase tracking-[0.5px]">
                      Email
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="tu@email.com"
                        autoComplete="email"
                        disabled={isLoading}
                        className="border-[#1e1e1e] focus-visible:ring-[#1e1e1e] font-light"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[12px] font-medium text-[#1e1e1e] uppercase tracking-[0.5px]">
                      Contraseña
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        autoComplete="current-password"
                        disabled={isLoading}
                        className="border-[#1e1e1e] focus-visible:ring-[#1e1e1e] font-light"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full bg-white text-[#1e1e1e] border border-[#1e1e1e] hover:bg-[#1e1e1e] hover:text-white transition-all text-[14px] font-medium uppercase tracking-[1px] h-12"
                disabled={isLoading}
              >
                {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
